import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AssessmentService } from '../services/AssessmentService';
import { GradingEngine } from '../services/GradingEngine';
import ExamLayout from './ExamLayout';
import { useExam } from './ExamContext';
import { v4 as generateUUID } from 'uuid';
import QuestionGroupRenderer from './QuestionGroupRenderer';
import QuestionPalette from './QuestionPalette';
import { useAutoScroll } from '../hooks/useAutoScroll';
import { useKeyboardNav } from '../hooks/useKeyboardNav';
import { FeatureFlags } from '../config/featureFlags';

/**
 * Inline helper utility to verify if a user profile qualifies as an eligible Instructor Student
 */
const isInstructorStudent = (user) => {
  if (!user) return false;
  const classification = user.studentClassification || user.studentType || user.role || '';
  const normalized = String(classification).toUpperCase();
  
  return (
    normalized === 'INSTRUCTOR' ||
    normalized === 'INSTRUCTOR_STUDENT' ||
    user.isInstructor === true ||
    normalized === 'TEACHER' ||
    normalized === 'ADMIN'
  );
};

const ReadingEditor = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  
  const { answers, setTimeLeft, setIsExamStarted, activeQuestionId, setActiveQuestionId } = useExam();
  
  useAutoScroll(activeQuestionId);
  
  const [exam, setExam] = useState(null);
  const [currentPassageIndex, setCurrentPassageIndex] = useState(0);
  const [examStatus, setExamStatus] = useState("IN_PROGRESS");

  const handleSubmit = useCallback(async () => {
    if (examStatus === "SUBMITTED") return;

    // Standardized token validation check to abort or return early if no valid token exists in local storage
    const token = localStorage.getItem('llap_access_token') || localStorage.getItem('llap_auth_token');
    if (!token) {
      console.warn('Submission aborted: No valid authorization token found.');
      navigate('/login');
      return;
    }

    try {
      setIsExamStarted(false);
      setTimeLeft(0);
      
      const subId = generateUUID();
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Validate user profile classifications and role claims using instructor policy logic and feature flags
      const isEligibleInstructor = isInstructorStudent(currentUser) && FeatureFlags.DIRECT_INSTRUCTOR_RESULT_RELEASE_ENABLED;

      // Dynamically calculate score and breakdown using GradingEngine against the active answer key
      const gradingResult = GradingEngine.calculateScore(answers, exam?.answerKey || {});
      const targetExamId = examId || exam?.examId || 'reading-1';

      const submissionData = {
        submissionId: subId,
        examId: targetExamId,
        assessmentName: `IELTS Academic | ${exam?.title || 'Reading Exam'}`,
        module: 'Reading',
        studentName: currentUser.fullName || currentUser.name || currentUser.email || 'Shahd Abdo',
        studentId: currentUser.id || currentUser.uid || 'student_auth_01',
        studentClassification: currentUser.studentClassification || currentUser.studentType || (isEligibleInstructor ? 'INSTRUCTOR' : 'PUBLIC'),
        rawScore: gradingResult.rawScore,          
        maxScore: gradingResult.maxScore || 40,          
        estimatedBand: gradingResult.estimatedBand,
        correctCount: gradingResult.rawScore,
        incorrectCount: gradingResult.incorrectCount,
        unansweredCount: gradingResult.unansweredCount,
        answers: answers,                                
        results: gradingResult.results,           
        status: isEligibleInstructor ? 'Released' : 'Pending Teacher Review',          
        reviewStatus: isEligibleInstructor ? 'DIRECT_INSTRUCTOR_STUDENT_RELEASE' : 'Pending Teacher Review',
        directRelease: isEligibleInstructor,
        submittedAt: new Date().toISOString()
      };

      // Try secure submit API
      try {
        await AssessmentService.secureSubmit(submissionData);
      } catch (netErr) {
        console.warn('Network submit fallback triggered, storing locally:', netErr);
      }

      // Persist submission locally for results hub or teacher queue synchronization, isolating instructor students
      const localStorageKey = isEligibleInstructor ? 'llap_instructor_student_submissions' : 'llap_teacher_submissions';
      const existingSubmissions = JSON.parse(localStorage.getItem(localStorageKey) || '[]');
      existingSubmissions.push(submissionData);
      localStorage.setItem(localStorageKey, JSON.stringify(existingSubmissions));

      setExamStatus("SUBMITTED");
      
      const resultsPath = (targetExamId === 'reading-2' || targetExamId === 'R2') ? 'performance/mock exam/reading-2' : 'performance/mock exam/reading 1';
      
      if (isEligibleInstructor) {
        alert(`Examination submitted successfully! Your result has been verified and directly released. You can view your results at ${resultsPath}.`);
        navigate(`/${resultsPath}`);
      } else {
        alert(`Examination submitted successfully! Your submission is now pending review by Teacher Shahd. Once verified and released, your results will be available at ${resultsPath}.`);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("Submission failed.");
    }
  }, [examStatus, exam, examId, answers, setIsExamStarted, setTimeLeft, navigate]);

  // Authoritative Server-Side Timer Enforcement & Trial Initialization Hook
  const handleSubmitRef = useRef(handleSubmit);
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  useEffect(() => {
    // Standardized token validation check to abort or return early if no valid token exists in local storage
    const token = localStorage.getItem('llap_access_token') || localStorage.getItem('llap_auth_token');
    if (!token) {
      navigate('/login');
      return;
    }

    const targetExamId = examId || 'reading-1';
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const studentId = currentUser.id || currentUser.uid || 'student_auth_01';

    // Clear any stale local countdown numbers to prevent 38-minute initialization bugs
    localStorage.removeItem('exam_timer_remaining');

    const attemptStorageKey = `active_exam_attempt_${targetExamId}_${studentId}`;
    let activeAttempt = JSON.parse(localStorage.getItem(attemptStorageKey) || 'null');

    // If no active attempt exists or it has expired, initialize a fresh trial via AssessmentService
    if (!activeAttempt || activeAttempt.status !== 'ACTIVE') {
      const initResult = AssessmentService.startReadingExam(targetExamId, studentId, currentUser);
      if (!initResult.success) {
        alert(initResult.message);
        navigate('/dashboard');
        return;
      }
      activeAttempt = initResult.attempt;
      localStorage.setItem(attemptStorageKey, JSON.stringify(activeAttempt));
    }

    // Decouple timer countdown by binding state directly to the authoritative server's expiresAt timestamp
    const expiresAtMs = new Date(activeAttempt.expiresAt).getTime();
    setIsExamStarted(true);

    const timerInterval = setInterval(() => {
      const now = Date.now();
      const remainingSeconds = Math.max(0, Math.floor((expiresAtMs - now) / 1000));

      setTimeLeft(remainingSeconds);

      if (remainingSeconds <= 0) {
        clearInterval(timerInterval);
        setIsExamStarted(false);
        localStorage.removeItem(attemptStorageKey);
        alert("Time is up! Your exam is being automatically submitted.");
        handleSubmitRef.current();
      }
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [examId, navigate, setIsExamStarted, setTimeLeft]);

  const handlePrevious = () => {
    if (examStatus === "IN_PROGRESS" && currentPassageIndex > 0) {
      setCurrentPassageIndex(currentPassageIndex - 1);
    }
  };

  const handleNext = () => {
    if (examStatus !== "IN_PROGRESS") return;
    if (exam && currentPassageIndex < exam.passages.length - 1) {
      setCurrentPassageIndex(currentPassageIndex + 1);
    } else {
      alert("You are on the final passage.");
    }
  };

  const handleJumpTo = (id) => {
    setActiveQuestionId(id);
  };

  useKeyboardNav(
    exam && currentPassageIndex < exam.passages.length - 1 ? handleNext : null,
    currentPassageIndex > 0 ? handlePrevious : null
  );

  // Dynamically resolve assessment package (Mock 1 vs Mock 2) based on route examId
  useEffect(() => {
    // Standardized token validation check to abort or return early if no valid token exists in local storage
    const token = localStorage.getItem('llap_access_token') || localStorage.getItem('llap_auth_token');
    if (!token) {
      return;
    }

    try {
      const targetExamId = examId || 'reading-1';
      const packageResult = AssessmentService.getAssessmentPackage(targetExamId);

      if (packageResult && packageResult.success) {
        setExam(packageResult.exam);
      } else {
        console.error("Exam package resolution failed for examId:", targetExamId);
        alert(`System Error: Failed to load assessment content for ${targetExamId}.`);
      }
    } catch (err) {
      console.error("Failed to load exam data packages:", err);
    }
  }, [examId]);

  const handleExit = () => {
    if (window.confirm("Are you sure? Your progress is saved, but you will be returned to the dashboard.")) {
      navigate('/dashboard');
    }
  };

  if (!exam) return <div style={{ padding: '60px', textAlign: 'center' }}>Loading and Validating Reading Exam Content...</div>;

  const passage = exam.passages[currentPassageIndex];
  const currentPassageQuestions = passage?.groups?.flatMap(g => g.questions) || [];

  return (
    <ExamLayout
      title={`IELTS Academic | ${exam.title}`}
      onExit={handleExit}
      onNext={currentPassageIndex < exam.passages.length - 1 ? handleNext : null}
      onPrevious={currentPassageIndex > 0 ? handlePrevious : null}
      onSubmit={handleSubmit}
    >
      <div style={{ padding: '20px' }}>
        {passage?.heading && (
          <h2 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#333', marginBottom: '8px', textTransform: 'uppercase' }}>
            {passage.heading}
          </h2>
        )}
        {passage?.instructionBanner && (
          <p style={{ fontStyle: 'italic', color: '#555', marginBottom: '15px', fontSize: '0.95rem' }}>
            {passage.instructionBanner}
          </p>
        )}

        <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '12px' }}>
          {passage?.title || "Loading..."}
        </h3>
        <p style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
          {passage?.text || ""}
        </p>
      </div>

      <div style={{ padding: '20px' }}>
        <h4>Questions</h4>
        <QuestionPalette
          questions={currentPassageQuestions}
          onJumpToQuestion={handleJumpTo}
        />
        {passage?.groups?.map((group) => (
          <QuestionGroupRenderer key={group.groupId} group={group} />
        ))}
      </div>
    </ExamLayout>
  );
};

export default ReadingEditor;